import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { authApi } from '../../api/auth';
import { LoginResponse } from '../../types';

interface TwoFactorVerifyProps {
  tempToken: string;
  onSuccess: (response: LoginResponse) => void;
  onCancel?: () => void;
}

export const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({
  tempToken,
  onSuccess,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [isBackupMode, setIsBackupMode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [isBackupMode]);

  const handleVerify = async (codeOverride?: string) => {
    const codeToVerify = (codeOverride ?? code).trim();

    if (!isBackupMode && codeToVerify.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    if (isBackupMode && codeToVerify.length !== 8) {
      setError('Please enter an 8-character backup code');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await authApi.verifyLogin2FA(tempToken, codeToVerify);
      onSuccess(response);
    } catch (err: unknown) {
      let errorMessage = 'Invalid verification code';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        const detail = axiosError.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      setError(errorMessage);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (value: string) => {
    if (isBackupMode) {
      // Backup codes are alphanumeric, 8 characters
      const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
      setCode(cleaned);
    } else {
      // TOTP codes are 6 digits
      const cleaned = value.replace(/\D/g, '').slice(0, 6);
      setCode(cleaned);

      // Auto-submit when 6 digits entered
      if (cleaned.length === 6) {
        setTimeout(() => {
          handleVerify(cleaned);
        }, 100);
      }
    }
    setError(null);
  };

  const toggleMode = () => {
    setIsBackupMode(!isBackupMode);
    setCode('');
    setError(null);
  };

  if (isVerifying) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
        <p>Verifying...</p>
      </div>
    );
  }

  return (
    <div className="two-factor-verify">
      <h2>Two-Factor Authentication</h2>

      {isBackupMode ? (
        <p>Enter one of your 8-character backup codes.</p>
      ) : (
        <p>Enter the 6-digit code from your authenticator app.</p>
      )}

      {error && <Message severity="error" text={error} className="field-message" />}

      <div className="two-factor-code-input">
        <InputText
          ref={inputRef}
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder={isBackupMode ? 'XXXXXXXX' : '000000'}
          maxLength={isBackupMode ? 8 : 6}
          disabled={isVerifying}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleVerify(code);
            }
          }}
        />
      </div>

      <Button
        label={isBackupMode ? 'Use authenticator app instead' : 'Use a backup code instead'}
        link
        onClick={toggleMode}
        className="two-factor-toggle-mode"
      />

      <div className="dialog-actions">
        {onCancel && (
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            onClick={onCancel}
            disabled={isVerifying}
          />
        )}
        <Button
          label="Verify"
          icon="ms-out ms-out-check"
          onClick={() => handleVerify()}
          disabled={
            isVerifying ||
            (!isBackupMode && code.length !== 6) ||
            (isBackupMode && code.length !== 8)
          }
          loading={isVerifying}
        />
      </div>
    </div>
  );
};

export default TwoFactorVerify;
